/**
 * Bonnell — Big Post widget market gate
 * =====================================================================
 * Big Post (Australia courier) injects `cis-shop.bigpost.com.au/js/shopify_widget.js`
 * on EVERY page of EVERY market through a Shopify app ScriptTag (loaded by
 * Shopify's `shopify.loadfeatures` async loader in <head>). Outside the
 * Australia storefront it has no product/widget context and throws
 * `Uncaught TypeError (...reading 'id')`.
 *
 * This guard allows Big Post ONLY on Australia (/en-au/) product pages.
 * On every other route/market it:
 *   1. Prevents the shopify_widget.js <script> from being inserted / fetched.
 *   2. Removes any Big Post widget node that slips into the DOM.
 * This removes the widget everywhere except AU product pages and eliminates
 * the console TypeError outside AU.
 *
 * Loaded via: theme.liquid  ->  <script src="{{ 'hidden-bigpost.js' | asset_url }}" defer>
 * The defer script runs after HTML parse and BEFORE window 'load' (when the
 * Shopify loader inserts the Big Post script), so the interception is in place
 * in time.
 *
 * Version: 2.0 — September 2026
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------
   * 1. Decide whether Big Post is allowed on this page.
   *    Allowed only when we are in the Australia market storefront
   *    (URL path carries the /en-au/ prefix) AND on a product page.
   * ------------------------------------------------------------- */
  function isAllowed() {
    var path = window.location.pathname || '';

    // Australia market signal. Primary: URL prefix. Secondary: Shopify.routes.root
    // (equals "/en-au/" inside the AU market) as a robustness cross-check.
    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var isAU = /^\/en-au(\/|$)/.test(path) || /^\/en-au\//.test(routeRoot);

    // Product page signal. The Big Post widget is only meaningful on PDPs.
    var isProduct = path.indexOf('/products/') !== -1;

    return isAU && isProduct;
  }

  var ALLOWED = isAllowed();

  function isBigPostUrl(src) {
    return typeof src === 'string' && src.indexOf('cis-shop.bigpost.com.au') !== -1;
  }

  if (ALLOWED) {
    // On AU product pages: do nothing. Let Big Post load and render normally.
    return;
  }

  /* ---------------------------------------------------------------
   * 2. Block the Big Post script from being inserted / fetched.
   *    Shopify's loader does:
   *      var s = document.createElement('script');
   *      s.src = urls[i];  // one of them is the Big Post URL
   *      x.parentNode.insertBefore(s, x);
   *    We intercept at the DOM-insertion layer (insertBefore/appendChild)
   *    so the <script> never enters the document and never fetches.
   * ------------------------------------------------------------- */
  function shouldBlockNode(node) {
    try {
      if (!node || node.nodeType !== 1) return false;
      if (node.tagName !== 'SCRIPT') return false;
      return isBigPostUrl(node.src || node.getAttribute('src'));
    } catch (e) {
      return false;
    }
  }

  var _insertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, refNode) {
    if (shouldBlockNode(newNode)) {
      // Neutralise: drop the src so no network request fires, and skip insertion.
      try { newNode.src = ''; } catch (e) {}
      return newNode;
    }
    return _insertBefore.call(this, newNode, refNode);
  };

  var _appendChild = Node.prototype.appendChild;
  Node.prototype.appendChild = function (newNode) {
    if (shouldBlockNode(newNode)) {
      try { newNode.src = ''; } catch (e) {}
      return newNode;
    }
    return _appendChild.call(this, newNode);
  };

  /* ---------------------------------------------------------------
   * 3. Fallback: remove any Big Post widget node or leftover script
   *    that reaches the DOM through a path we did not intercept.
   * ------------------------------------------------------------- */
  var WIDGET_SELECTORS = [
    '#bigpost-custom-widget',
    '#bigpost-widget',
    '[id^="bigpost-"]',
    'script[src*="cis-shop.bigpost.com.au"]'
  ];

  function sweep() {
    try {
      document.querySelectorAll(WIDGET_SELECTORS.join(',')).forEach(function (el) {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
    } catch (e) {}
  }

  function start() {
    sweep();
    var observer = new MutationObserver(function () { sweep(); });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    // Stop observing after the page has settled to avoid a permanent observer.
    window.addEventListener('load', function () {
      setTimeout(function () { observer.disconnect(); sweep(); }, 4000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
